import { socialProviders } from "@daveyplate/better-auth-ui";
import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { fetchInstallableConnectorCatalog } from "@/lib/api/connections";
import { API_URL, fetchWithTimeout } from "@/lib/api/core";
import { authClient, organization, signIn, signOut } from "@/lib/auth";

/** Sentinel `<Select>` value for the "create a new organization" option. */
const NEW_ORG_VALUE = "__new_org__";

/** Lowercase, hyphenate, strip to a URL-safe org slug. */
function slugify(value: string): string {
	return value
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
}

/**
 * Provider-agnostic connection "claim" page. A provider-initiated (marketplace)
 * install lands as an org-less pending row; this page confirms the destination
 * org and binds it, once the provider's authority check passes. Slack is the
 * first consumer (`/connector/slack/connection?ref=<team>`); the copy, sign-in
 * provider, and subject wording are all driven by the claim-context response.
 */
export const Route = createFileRoute("/connector/$name/connection")({
	component: ConnectorConnectionPage,
	validateSearch: (search: Record<string, unknown>) => ({
		ref: typeof search.ref === "string" ? search.ref : undefined,
		// Better Auth appends `error=<code>` to `errorCallbackURL` when a sign-in
		// or account link fails, which lands the person back on this claim.
		...(typeof search.error === "string" ? { error: search.error } : {}),
	}),
});

interface ClaimOrg {
	id: string;
	slug: string;
	name: string;
	/** The claimer's personal org — sorted last and flagged as a last resort. */
	isPersonal: boolean;
}

/** The identity of the OTHER org a subject already conflicts with. */
interface ClaimForeignBinding {
	orgSlug: string | null;
	orgName: string | null;
	/**
	 * Which cross-org fence tripped: `same_workspace` (a deliberate, overridable
	 * MOVE) vs `enterprise_scope_overlap` (a Grid routing collision, NOT
	 * overridable). Mirrors the server's `ClaimConflictKind`.
	 */
	matchKind: "same_workspace" | "enterprise_scope_overlap";
}

/** The error shape carries the optional signin/authorize/conflict payload inline. */
interface ClaimErrorBody {
	error: string;
	/** Present on `signin_required` — which provider the user must sign in with. */
	signinProvider?: string;
	/** Present on `not_authorized` — the provider-specific denial reason. */
	code?: string;
	/**
	 * Present on the two 409 cross-org conflicts (`already_connected_elsewhere`
	 * and `enterprise_scope_overlap`) — the other org that already holds this
	 * subject.
	 */
	existing?: ClaimForeignBinding;
}

/**
 * A cross-org 409 the confirm step surfaces for explicit resolution:
 *  - `already_connected_elsewhere`: the SAME workspace is active in `existing`'s
 *    org. Overridable — the user can MOVE it here (re-POST with confirmMove).
 *  - `enterprise_scope_overlap`: a Grid org-wide vs per-workspace routing
 *    collision. NOT overridable — blocking message, no move button.
 */
interface ClaimConflict {
	kind: "already_connected_elsewhere" | "enterprise_scope_overlap";
	existing: ClaimForeignBinding;
}

type ContextResponse =
	| {
			ok: true;
			subjectKind: string;
			subjectName: string | null;
			orgs: ClaimOrg[];
	  }
	| {
			ok: true;
			alreadyConnected: true;
			orgSlug: string | null;
			nextUrl?: string;
	  }
	| ClaimErrorBody;

type ClaimResponse =
	| { ok: true; orgSlug: string | null; nextUrl?: string }
	| ClaimErrorBody;

type View =
	| { kind: "loading" }
	| { kind: "error"; message: string }
	| { kind: "signin"; signinProvider: string; linkAccount: boolean }
	/** Verifying the provider identity failed; `error` is Better Auth's code. */
	| { kind: "link_failed"; signinProvider: string; error: string }
	| { kind: "already_connected"; orgSlug: string | null; nextUrl?: string }
	| {
			kind: "confirm";
			subjectKind: string;
			subjectName: string | null;
			orgs: ClaimOrg[];
	  };

/** Sign-in provider name (`google` → `Google`) from the auth UI's provider list. */
function signinProviderName(provider: string): string {
	return socialProviders.find((known) => known.provider === provider)?.name ?? provider;
}

/** This claim's own URL: where every sign-in and link returns, success or not. */
function claimUrl(name: string, ref: string): string {
	return `${window.location.origin}/connector/${encodeURIComponent(name)}/connection?${new URLSearchParams({ ref })}`;
}

/** Why verifying the provider identity sent the person back here. */
function linkFailedText(error: string, provider: string): string {
	if (error === "account_already_linked_to_different_user") {
		return `This ${provider} account belongs to a different Lobu account than the one you are signed in to. Sign out, then continue with ${provider} to finish setup as that account.`;
	}
	return `We couldn’t verify your ${provider} account for this setup. Sign out, then continue with ${provider} to try again.`;
}

/**
 * The connector's display name from the catalog of an org the claimer belongs
 * to. Null until known: copy then stays generic rather than guessing a name
 * from the key.
 */
function useConnectorName(connectorKey: string, orgSlug: string | null): string | null {
	const [names, setNames] = useState<Record<string, string | null>>({});
	const cacheKey = orgSlug ? `${orgSlug}:${connectorKey}` : null;
	useEffect(() => {
		if (!cacheKey || !orgSlug || cacheKey in names) return;
		let cancelled = false;
		fetchInstallableConnectorCatalog(orgSlug)
			.then((items) => items.find((item) => item.key === connectorKey)?.name ?? null)
			.catch(() => null)
			.then((found) => {
				if (!cancelled) setNames((current) => ({ ...current, [cacheKey]: found }));
			});
		return () => {
			cancelled = true;
		};
	}, [cacheKey, connectorKey, names, orgSlug]);
	return cacheKey ? (names[cacheKey] ?? null) : null;
}

function claimDestination(
	orgSlug: string | null,
	provider: string,
	nextUrl?: string,
): string {
	if (!orgSlug) return "/";
	const orgPath = `/${encodeURIComponent(orgSlug)}/`;
	if (nextUrl?.startsWith(orgPath)) {
		const destination = new URL(nextUrl, window.location.origin);
		if (
			destination.origin === window.location.origin &&
			destination.pathname.startsWith(orgPath)
		)
			return destination.pathname + destination.search;
	}
	return `${orgPath}connectors/${encodeURIComponent(provider)}?connected=1`;
}

/** Human copy for a provider `not_authorized` reason code. */
function authorizeErrorText(code: string | undefined, subject: string): string {
	if (code === "not_admin") {
		return `You must be an admin or owner to connect ${subject} to Lobu.`;
	}
	return `You don't have permission to connect ${subject} to Lobu.`;
}

function friendlyError(
	error: string,
	code: string | undefined,
	subject: string,
): string {
	switch (error) {
		case "not_authorized":
			return authorizeErrorText(code, subject);
		case "no_pending":
			return `No Lobu install was found for ${subject}. Add Lobu from the marketplace to get started.`;
		case "no_org":
			return `Your account has no Lobu organization to connect ${subject} to.`;
		default:
			return `Something went wrong while connecting ${subject}. Please try again.`;
	}
}

function ConnectorConnectionPage() {
	const { name } = useParams({ from: "/connector/$name/connection" });
	const { ref, error: authError } = useSearch({
		from: "/connector/$name/connection",
	});
	const [view, setView] = useState<View>({ kind: "loading" });
	const [selectedOrg, setSelectedOrg] = useState("");
	const [newOrgName, setNewOrgName] = useState("");
	const [connecting, setConnecting] = useState(false);
	const [connectError, setConnectError] = useState<string | null>(null);
	// A cross-org 409 awaiting the user's decision (move it here / blocked).
	// Separate from `connectError` so we can render a dedicated prompt with the
	// other org's name and (for the overridable case) a "Move it here" action.
	const [conflict, setConflict] = useState<ClaimConflict | null>(null);

	const providerLabel = useConnectorName(
		name,
		view.kind === "confirm"
			? (view.orgs[0]?.slug ?? null)
			: view.kind === "already_connected"
				? view.orgSlug
				: null,
	);
	// The subject noun for copy ("workspace" for Slack, "account" for a per-user
	// connector). Comes from the context response; a neutral default covers the
	// pre-context view states (loading/signin/error) where it isn't known yet.
	const subjectNoun =
		view.kind === "confirm" ? view.subjectKind || "connection" : "connection";
	const subjectLabel =
		view.kind === "confirm" && view.subjectName
			? view.subjectName
			: providerLabel
				? `this ${providerLabel} ${subjectNoun}`
				: `this ${subjectNoun}`;

	const loadContext = useCallback(async () => {
		if (!ref) {
			setView({
				kind: "error",
				message: "This connection link is missing its reference.",
			});
			return;
		}
		setView({ kind: "loading" });
		try {
			const res = await fetchWithTimeout(
				`${API_URL}/api/connector/${encodeURIComponent(name)}/connection/claim-context?ref=${encodeURIComponent(ref)}`,
				{ credentials: "include" },
			);
			const body = (await res.json()) as ContextResponse;
			if ("error" in body) {
				if (body.error === "unauthenticated") {
					setView({
						kind: "signin",
						signinProvider: body.signinProvider ?? name,
						linkAccount: false,
					});
					return;
				}
				if (body.error === "signin_required") {
					setView(
						authError
							? {
									kind: "link_failed",
									signinProvider: body.signinProvider ?? name,
									error: authError,
								}
							: {
									kind: "signin",
									signinProvider: body.signinProvider ?? name,
									linkAccount: true,
								},
					);
					return;
				}
				setView({
					kind: "error",
					message: friendlyError(body.error, body.code, "this connection"),
				});
				return;
			}
			if ("alreadyConnected" in body) {
				setView({
					kind: "already_connected",
					orgSlug: body.orgSlug,
					nextUrl: body.nextUrl,
				});
				return;
			}
			setSelectedOrg(body.orgs[0]?.slug ?? "");
			setView({
				kind: "confirm",
				subjectKind: body.subjectKind,
				subjectName: body.subjectName,
				orgs: body.orgs,
			});
		} catch {
			setView({
				kind: "error",
				message: "We couldn’t load this connection link. Please try again.",
			});
		}
	}, [name, ref, authError]);

	useEffect(() => {
		void loadContext();
	}, [loadContext]);

	const handleSignIn = async (signinProvider: string) => {
		if (!ref) return;
		// Success and failure both return to this claim (never the signed-in
		// account's home), without the error code of an earlier attempt.
		const returnTo = claimUrl(name, ref);
		if (view.kind === "signin" && view.linkAccount) {
			const result = await authClient.linkSocial({
				provider: signinProvider,
				callbackURL: returnTo,
				errorCallbackURL: returnTo,
			});
			if (result.error)
				setView({
					kind: "error",
					message: result.error.message ?? "Couldn’t verify this account.",
				});
			return;
		}
		// The identity belongs to another Lobu account: leave this session and
		// sign in as that account instead of linking it here.
		if (view.kind === "link_failed") await signOut();
		await signIn.social({
			provider: signinProvider as Parameters<
				typeof signIn.social
			>[0]["provider"],
			callbackURL: returnTo,
			errorCallbackURL: returnTo,
		});
	};

	// `confirmMove` re-issues the claim to MOVE a workspace already active in
	// another org into the confirmed org — only reachable from the
	// `already_connected_elsewhere` prompt's "Move it here" button.
	const handleConnect = async (confirmMove = false) => {
		if (!ref || !selectedOrg) return;
		setConnecting(true);
		setConnectError(null);
		setConflict(null);
		try {
			// "New organization" chosen: create it first (the caller becomes its
			// owner, so the claim's membership check passes), then bind into it.
			let targetOrgSlug = selectedOrg;
			if (selectedOrg === NEW_ORG_VALUE) {
				const trimmed = newOrgName.trim();
				if (!trimmed) {
					setConnectError("Enter a name for the new organization.");
					return;
				}
				const created = await organization.create({
					name: trimmed,
					slug: slugify(trimmed),
				});
				if (created.error || !created.data) {
					setConnectError(
						created.error?.message ?? "Couldn’t create the organization.",
					);
					return;
				}
				targetOrgSlug = created.data.slug;
			}
			const res = await fetchWithTimeout(
				`${API_URL}/api/connector/${encodeURIComponent(name)}/connection/claim`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({ ref, org: targetOrgSlug, confirmMove }),
				},
			);
			const body = (await res.json()) as ClaimResponse;
			if ("error" in body) {
				if (
					body.error === "signin_required" ||
					body.error === "unauthenticated"
				) {
					setView({
						kind: "signin",
						signinProvider: body.signinProvider ?? name,
						linkAccount: body.error === "signin_required",
					});
					return;
				}
				// The two DISTINCT cross-org 409s the server keeps distinguishable via
				// `error` + `existing`. Surface them as a decision prompt, not a generic
				// error: `already_connected_elsewhere` is an overridable MOVE, while
				// `enterprise_scope_overlap` is a blocking routing collision.
				if (
					(body.error === "already_connected_elsewhere" ||
						body.error === "enterprise_scope_overlap") &&
					body.existing
				) {
					setConflict({ kind: body.error, existing: body.existing });
					return;
				}
				setConnectError(friendlyError(body.error, body.code, subjectLabel));
				return;
			}
			window.location.href = claimDestination(body.orgSlug, name, body.nextUrl);
		} catch {
			setConnectError(
				`Something went wrong while connecting this ${subjectNoun}. Please try again.`,
			);
		} finally {
			setConnecting(false);
		}
	};

	const title =
		view.kind === "signin"
			? "Sign in to continue"
			: view.kind === "link_failed"
				? "Use a different account"
				: view.kind === "error"
					? `Can’t connect this ${subjectNoun}`
					: view.kind === "already_connected"
						? "Already connected"
						: providerLabel
							? `Connect ${providerLabel} to Lobu`
							: "Connect to Lobu";

	return (
		<div className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center">
			<div className="w-full max-w-md p-8">
				<Card>
					<CardHeader>
						<CardTitle asChild>
							<h1>{title}</h1>
						</CardTitle>
					</CardHeader>
					<CardContent>
						{view.kind === "loading" && (
							<div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
								<Spinner size="lg" />
								<span>Checking this connection link…</span>
							</div>
						)}

						{view.kind === "error" && (
							<div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
								{view.message}
							</div>
						)}

						{view.kind === "already_connected" && (
							<div className="space-y-4">
								<p className="text-sm text-muted-foreground">
									This {providerLabel ? `${providerLabel} ` : ""}
									{subjectNoun} is already connected to Lobu.
								</p>
								<Button className="w-full" asChild>
									<a href={claimDestination(view.orgSlug, name, view.nextUrl)}>
										{view.nextUrl ? "Choose an agent" : "Open Lobu"}
									</a>
								</Button>
							</div>
						)}

						{view.kind === "signin" && (
							<div className="space-y-4">
								<p className="text-sm text-muted-foreground">
									Sign in with {signinProviderName(view.signinProvider)} to
									verify you can connect this {subjectNoun}.
								</p>
								<Button
									className="w-full"
									onClick={() => handleSignIn(view.signinProvider)}
								>
									Sign in with {signinProviderName(view.signinProvider)}
								</Button>
							</div>
						)}

						{view.kind === "link_failed" && (
							<div className="space-y-4">
								<div
									role="alert"
									className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
								>
									{linkFailedText(
										view.error,
										signinProviderName(view.signinProvider),
									)}
								</div>
								<Button
									className="w-full"
									onClick={() => handleSignIn(view.signinProvider)}
								>
									Sign out and continue with{" "}
									{signinProviderName(view.signinProvider)}
								</Button>
							</div>
						)}

						{view.kind === "confirm" &&
							(() => {
								const subjectDisplay = view.subjectName || subjectLabel;
								return (
									<div className="space-y-4">
										<p className="text-sm text-muted-foreground">
											Connecting{" "}
											<strong className="font-medium text-foreground">
												{subjectDisplay}
											</strong>
											. Choose the organization to connect it to:
										</p>
										<Select
											value={selectedOrg}
											onValueChange={(v) => {
												// A conflict is tied to the previously chosen org; picking
												// a different destination clears it so the next attempt is
												// re-evaluated against the new org.
												setConflict(null);
												setSelectedOrg(v);
											}}
										>
											<SelectTrigger>
												<SelectValue placeholder="Select an organization" />
											</SelectTrigger>
											<SelectContent>
												{view.orgs.map((org) => (
													<SelectItem key={org.id} value={org.slug}>
														{org.name}
														{org.isPersonal && (
															<span className="ml-1 text-muted-foreground">
																(personal)
															</span>
														)}
													</SelectItem>
												))}
												<SelectItem value={NEW_ORG_VALUE}>
													+ New organization
												</SelectItem>
											</SelectContent>
										</Select>
										{selectedOrg === NEW_ORG_VALUE && (
											<Input
												autoFocus
												value={newOrgName}
												onChange={(e) => setNewOrgName(e.target.value)}
												placeholder="New organization name"
												aria-label="New organization name"
											/>
										)}
										{connectError && (
											<div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
												{connectError}
											</div>
										)}
										{conflict &&
											(() => {
												const otherOrg =
													conflict.existing.orgName ??
													conflict.existing.orgSlug ??
													"another organization";
												if (conflict.kind === "already_connected_elsewhere") {
													// Overridable: the SAME workspace is active elsewhere.
													// Offer to MOVE it here (re-POST with confirmMove).
													return (
														<div className="space-y-3 rounded-lg border border-border bg-muted/40 px-3 py-3">
															<p className="text-sm text-foreground">
																{subjectDisplay} is already connected in{" "}
																<strong className="font-medium">
																	{otherOrg}
																</strong>
																. Move it here?
															</p>
															<div className="flex gap-2">
																<Button
																	className="flex-1"
																	onClick={() => handleConnect(true)}
																	disabled={connecting}
																>
																	{connecting ? (
																		<>
																			<Spinner size="lg" />
																			<span className="ml-2">Moving…</span>
																		</>
																	) : (
																		"Move it here"
																	)}
																</Button>
																<Button
																	className="flex-1"
																	variant="outline"
																	onClick={() => setConflict(null)}
																	disabled={connecting}
																>
																	Cancel
																</Button>
															</div>
														</div>
													);
												}
												// enterprise_scope_overlap: NOT overridable — blocking
												// message, no move button.
												return (
													<div className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
														This {providerLabel ? `${providerLabel} ` : ""}
														Enterprise Grid is already connected in{" "}
														<strong className="font-medium">{otherOrg}</strong>
														{"; "}
														connecting this {subjectNoun} here may change how
														its messages route. Contact support to move it.
													</div>
												);
											})()}
										<Button
											className="w-full"
											onClick={() => handleConnect()}
											disabled={
												connecting ||
												conflict !== null ||
												!selectedOrg ||
												(selectedOrg === NEW_ORG_VALUE && !newOrgName.trim())
											}
										>
											{connecting ? (
												<>
													<Spinner size="lg" />
													<span className="ml-2">Connecting…</span>
												</>
											) : (
												`Connect ${subjectDisplay}`
											)}
										</Button>
									</div>
								);
							})()}
					</CardContent>
				</Card>
			</div>
		</div>
	);
}
